import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext/AuthContext";

const PRIMARY        = "#009DB9";
const PRIMARY_DARK   = "#007d94";
const PRIMARY_LIGHT  = "rgba(0,157,185,0.10)";
const PRIMARY_BORDER = "rgba(0,157,185,0.22)";
const WARNING_THRESHOLD_MS = 30 * 60 * 1000;
const TOUR_KEY = "hasCompletedTour";

function getTimeOfDay() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

function getTodayString() {
  return new Date().toLocaleDateString("en-US", {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
  });
}

const TOUR_STEPS = [
  {
    target: "welcome",
    title: "Welcome to IZSoftwares Portal! 👋",
    content: "Let's take a quick 30-seconds tour.",
    position: "bottom",
  },
  {
    target: "modules",
    title: "Main Page",
    content: "This is your navigation. It provides you with access to various IZSoftwares App.",
    position: "top",
  },
  {
    target: "navbar-timer",
    title: "NavBar",
    content: "Looking for disconnecting or view the time before the Automatic Logout.",
    position: "bottom",
  },
];

function TourOverlay({ stepIndex, onNext, onBack, onSkip, onFinish }) {
  const step = TOUR_STEPS[stepIndex];
  const isLast = stepIndex === TOUR_STEPS.length - 1;
  const [tooltipStyle, setTooltipStyle] = useState({});
  const [spotlightStyle, setSpotlightStyle] = useState({});
  const tooltipRef = useRef(null);

  useEffect(() => {
    const el = document.querySelector(`[data-tour="${step.target}"]`);
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const scrollY = window.scrollY;
    const pad = 10;

    setSpotlightStyle({
      position: "fixed",
      top: rect.top - pad,
      left: rect.left - pad,
      width: rect.width + pad * 2,
      height: rect.height + pad * 2,
      borderRadius: 14,
      boxShadow: `0 0 0 3px ${PRIMARY}, 0 0 0 9999px rgba(0,0,0,0.6)`,
      zIndex: 9998,
      pointerEvents: "none",
    });

    const tooltipWidth = Math.min(340, window.innerWidth - 32);
    let top, left;

    if (step.position === "bottom") {
      top = rect.bottom + scrollY + pad + 10;
      left = rect.left + rect.width / 2 - tooltipWidth / 2;
    } else {
      top = rect.top + scrollY - pad - 10 - 220;
      left = rect.left + rect.width / 2 - tooltipWidth / 2;
    }

    left = Math.max(16, Math.min(left, window.innerWidth - tooltipWidth - 16));

    setTooltipStyle({
      position: "absolute",
      top,
      left,
      width: tooltipWidth,
      zIndex: 9999,
    });

    el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [stepIndex]);

  useEffect(() => {
    function handleKey(e) {
      if (e.key === "Enter") isLast ? onFinish() : onNext();
      if (e.key === "Escape") onSkip();
    }
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [isLast, onNext, onFinish, onSkip]);

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 9997 }}>
      <div style={spotlightStyle} />
      <div style={{ position: "absolute", inset: 0, zIndex: 9999, pointerEvents: "none" }}>
        <div
          ref={tooltipRef}
          style={{
            ...tooltipStyle,
            pointerEvents: "all",
            backgroundColor: "#fff",
            borderRadius: 20,
            boxShadow: "0 20px 60px rgba(0,157,185,0.22), 0 4px 16px rgba(0,0,0,0.10)",
            overflow: "hidden",
            fontFamily: "inherit",
          }}
        >
          <div style={{ height: 4, background: `linear-gradient(90deg, ${PRIMARY}, #00c4e0)` }} />
          <div style={{ padding: "24px 24px 20px" }}>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <span style={{
                fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase",
                color: PRIMARY, backgroundColor: "rgba(0,157,185,0.08)",
                border: "1px solid rgba(0,157,185,0.18)", borderRadius: 20, padding: "3px 10px",
              }}>
                Step {stepIndex + 1} of {TOUR_STEPS.length}
              </span>
              <button
                onClick={onSkip}
                style={{ background: "none", border: "none", cursor: "pointer", fontSize: 11, fontWeight: 600, color: "#9ca3af", padding: "3px 6px", borderRadius: 6 }}
              >
                Skip tour ✕
              </button>
            </div>

            <h3 style={{ margin: "0 0 8px", fontSize: 17, fontWeight: 800, color: "#111827", lineHeight: 1.3 }}>
              {step.title}
            </h3>
            <div style={{ fontSize: 13.5, lineHeight: 1.65, color: "#6b7280", marginBottom: 20 }}>
              {step.content}
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 18 }}>
              {TOUR_STEPS.map((_, i) => (
                <div key={i} style={{
                  width: i === stepIndex ? 18 : 6, height: 6, borderRadius: 3,
                  backgroundColor: i === stepIndex ? PRIMARY : "rgba(0,157,185,0.2)",
                  transition: "all 0.3s ease",
                }} />
              ))}
            </div>

            <div style={{ display: "flex", gap: 10 }}>
              {stepIndex > 0 && (
                <button
                  onClick={onBack}
                  style={{ flex: 1, padding: "10px 16px", fontSize: 13, fontWeight: 600, color: "#6b7280", backgroundColor: "transparent", border: "1.5px solid #e5e7eb", borderRadius: 12, cursor: "pointer" }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#f9fafb"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
                >
                  ← Back
                </button>
              )}
              <button
                onClick={isLast ? onFinish : onNext}
                style={{ flex: 1, padding: "10px 16px", fontSize: 13, fontWeight: 700, color: "#fff", backgroundColor: PRIMARY, border: "none", borderRadius: 12, cursor: "pointer" }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = PRIMARY_DARK)}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = PRIMARY)}
              >
                {isLast ? "🎉 Let's go!" : "Next →"}
              </button>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}

function ModuleCard({ icon, title, description, pills, onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="relative flex flex-col overflow-hidden bg-white cursor-pointer group rounded-2xl"
      style={{
        border: `1.5px solid ${hovered ? PRIMARY : "#e9ecef"}`,
        boxShadow: hovered ? `0 16px 48px rgba(0,157,185,0.18), 0 2px 8px rgba(0,0,0,0.04)` : "0 2px 12px rgba(0,0,0,0.05)",
        transform: hovered ? "translateY(-6px)" : "translateY(0)",
        transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
      }}
    >
      <div className="w-full h-1 transition-all duration-300" style={{ background: hovered ? `linear-gradient(90deg, ${PRIMARY}, #00c4e0)` : "transparent" }} />
      <div className="flex flex-col flex-1 p-6">
        <div className="flex justify-end mb-5">
          <div className="flex items-center justify-center w-8 h-8 transition-all duration-300 rounded-full"
            style={{ backgroundColor: hovered ? PRIMARY : "rgba(0,157,185,0.08)", transform: hovered ? "rotate(0deg)" : "rotate(-45deg)" }}>
            <svg className="w-4 h-4" style={{ color: hovered ? "white" : PRIMARY }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>
            </svg>
          </div>
        </div>
        <div className="flex items-center justify-center w-16 h-16 mb-4 transition-all duration-300 rounded-2xl"
          style={{ backgroundColor: hovered ? "rgba(0,157,185,0.1)" : "#f4fcfd", border: `1.5px solid ${hovered ? "rgba(0,157,185,0.25)" : "#e4f5f8"}` }}>
          {icon ? (
            <img src={icon} alt={title} className="object-contain w-9 h-9" />
          ) : (
            <svg className="w-9 h-9" viewBox="0 0 24 24" fill="none" stroke={PRIMARY} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              <path d="M12 13a2 2 0 100-4 2 2 0 000 4z" />
            </svg>
          )}
        </div>
        <h2 className="mb-2 text-xl font-extrabold tracking-tight transition-colors duration-200" style={{ color: hovered ? PRIMARY : "#111827" }}>{title}</h2>
        <p className="flex-1 mb-5 text-sm leading-relaxed text-gray-400">{description}</p>
        <div className="flex flex-wrap gap-2">
          {pills.map((pill) => (
            <span key={pill} className="px-3 py-1 text-xs font-semibold transition-all duration-200 rounded-full"
              style={{
                color: hovered ? "#006b80" : "#5ab8cc",
                backgroundColor: hovered ? "rgba(0,157,185,0.1)" : "#f0fafc",
                border: `1px solid ${hovered ? "rgba(0,157,185,0.25)" : "#d5eef3"}`,
              }}>
              {pill}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { session, isAuthenticated, loading, logout, recordVisit } = useAuth();
  const navigate = useNavigate();

  const [now, setNow] = useState(Date.now());
  const [warningDismissed, setWarningDismissed] = useState(false);
  const hasInitialised = useRef(false);

  const [tourActive, setTourActive] = useState(false);
  const [tourStep, setTourStep] = useState(0);
  const [hasSkippedOrCompletedTour, setHasSkippedOrCompletedTour] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!loading && !isAuthenticated) navigate("/");
  }, [loading, isAuthenticated, navigate]);

  useEffect(() => {
    if (!session || hasInitialised.current) return;
    hasInitialised.current = true;
    recordVisit();
  }, [session, recordVisit]);

  // Show tour ONLY on first login (when isReturning is false)
  useEffect(() => {
    if (!session) return;
    
    // Check if tour has been completed before
    const done = localStorage.getItem(TOUR_KEY) === "true";
    
    // Only show tour if:
    // 1. Tour not done yet
    // 2. User is NOT returning (first time login)
    // 3. Tour hasn't been skipped/completed in this session
    if (!done && !session.isReturning && !hasSkippedOrCompletedTour) {
      const t = setTimeout(() => setTourActive(true), 600);
      return () => clearTimeout(t);
    }
  }, [session, hasSkippedOrCompletedTour]);

  const endTour = useCallback(() => {
    localStorage.setItem(TOUR_KEY, "true");
    setTourActive(false);
    setTourStep(0);
    setHasSkippedOrCompletedTour(true);
  }, []);

  const handleNext = useCallback(() => setTourStep((s) => s + 1), []);
  const handleBack = useCallback(() => setTourStep((s) => s - 1), []);

  if (loading || !session) return null;

  const remainingMs = Math.max(0, session.expiresAt - now);
  const totalSecs = Math.floor(remainingMs / 1000);
  const hrs = Math.floor(totalSecs / 3600);
  const mins = Math.floor((totalSecs % 3600) / 60);
  const secs = totalSecs % 60;
  const timeString = `${String(hrs).padStart(2, "0")}:${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  const isExpiring = remainingMs <= WARNING_THRESHOLD_MS && remainingMs > 0;
  const showWarning = isExpiring && !warningDismissed;

  // Use name from session (fetched from backend)
  const name = session.name || null;
  const greeting = session.isReturning
    ? (name ? `Welcome back, ${name} 👋` : `Welcome back 👋`)
    : (name ? `Welcome, ${name} 👋` : `${getTimeOfDay()} 👋`);

  function handleLogout() { logout(); navigate("/"); }
  function handleOpenModule(route, isExternal = false, externalUrl = "") {
    isExternal ? window.open(externalUrl, "_blank", "noopener,noreferrer") : navigate(route);
  }

  return (
    <div className="flex flex-col min-h-screen" style={{ backgroundColor: "#f0f9fb" }}>

      {tourActive && (
        <TourOverlay
          stepIndex={tourStep}
          onNext={handleNext}
          onBack={handleBack}
          onSkip={endTour}
          onFinish={endTour}
        />
      )}

      {showWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ backgroundColor: "rgba(0,0,0,0.5)", backdropFilter: "blur(6px)" }}>
          <div className="relative w-full max-w-sm p-8 mx-4 overflow-hidden bg-white shadow-2xl rounded-3xl">
            <div className="absolute top-0 left-0 right-0 h-1 rounded-t-3xl"
              style={{ background: "linear-gradient(90deg, #f59e0b, #ef4444)" }} />
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-5 rounded-2xl"
              style={{ backgroundColor: "#fff7ed", border: "1.5px solid #fed7aa" }}>
              <svg className="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
              </svg>
            </div>
            <h2 className="mb-2 text-xl font-extrabold text-center text-gray-900">Session Expiring Soon</h2>
            <p className="mb-2 text-sm leading-relaxed text-center text-gray-400">Your session expires in under <span className="font-bold text-orange-500">30 minutes</span>.</p>
            <p className="mb-6 text-sm leading-relaxed text-center text-gray-400">Please save any work in progress. You will need to log in again to continue.</p>
            <div className="flex items-center justify-center gap-2.5 mb-7 py-3 rounded-xl" style={{ backgroundColor: "#fff7ed", border: "1px solid #fed7aa" }}>
              <span className="relative flex w-2 h-2">
                <span className="absolute inline-flex w-full h-full bg-red-400 rounded-full animate-ping opacity-60" />
                <span className="relative inline-flex w-2 h-2 bg-red-500 rounded-full" />
              </span>
              <span className="text-lg font-extrabold tracking-widest text-red-500 tabular-nums">{timeString}</span>
            </div>
            <button onClick={() => setWarningDismissed(true)}
              className="w-full py-3 text-sm font-bold text-white transition-all duration-200 rounded-xl"
              style={{ backgroundColor: "#f59e0b" }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#d97706"; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "#f59e0b"; }}>
              Got it, dismiss
            </button>
          </div>
        </div>
      )}

      <nav className="sticky top-0 z-40 w-full bg-white"
        style={{ borderBottom: `1px solid ${PRIMARY_BORDER}`, boxShadow: "0 1px 12px rgba(0,157,185,0.06)" }}>
        <div className="flex items-center justify-between h-16 px-8 mx-auto max-w-7xl">
          <img src="/IZ-Logo.png" alt="IZSoftwares" className="object-contain w-auto h-9" />
          <div
            data-tour="navbar-timer"
            className="flex items-center gap-2.5 px-4 py-2 rounded-full transition-all duration-500"
            style={{
              backgroundColor: isExpiring ? "rgba(239,68,68,0.06)" : "rgba(0,157,185,0.07)",
              border: `1px solid ${isExpiring ? "rgba(239,68,68,0.25)" : PRIMARY_BORDER}`,
            }}
          >
            <span className="relative flex w-2 h-2">
              <span className="absolute inline-flex w-full h-full rounded-full animate-ping opacity-60" style={{ backgroundColor: isExpiring ? "#ef4444" : PRIMARY }} />
              <span className="relative inline-flex w-2 h-2 rounded-full" style={{ backgroundColor: isExpiring ? "#ef4444" : PRIMARY }} />
            </span>
            <span className="text-sm font-bold tabular-nums" style={{ color: isExpiring ? "#dc2626" : PRIMARY }}>{timeString}</span>
          </div>
          <button onClick={handleLogout}
            className="flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold transition-all duration-200"
            style={{ color: PRIMARY, border: `1.5px solid ${PRIMARY_BORDER}`, backgroundColor: "transparent" }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = PRIMARY_LIGHT; e.currentTarget.style.borderColor = PRIMARY; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.borderColor = PRIMARY_BORDER; }}>
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            Logout
          </button>
        </div>
      </nav>

      <div className="relative w-full overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, #007d94 60%, #005f72 100%)` }}>
        <div className="absolute w-64 h-64 bg-white rounded-full -top-16 -right-16 opacity-10" />
        <div className="absolute w-48 h-48 bg-white rounded-full -bottom-20 -left-10 opacity-10" />
        <div className="absolute w-24 h-24 bg-white rounded-full top-4 right-64 opacity-5" />
        <div data-tour="welcome" className="relative z-10 px-8 py-10 mx-auto max-w-7xl">
          <p className="mb-2 text-sm font-semibold opacity-80" style={{ color: "#b2eaf4" }}>{getTodayString()}</p>
          <h1 className="mb-1 text-3xl font-extrabold tracking-tight text-white">{greeting}</h1>
          <p className="text-sm opacity-70" style={{ color: "#cdf0f8" }}>Select a subsystem below to get started. All activity is session-protected.</p>
        </div>
      </div>

      <main className="flex-1 w-full">
        <div className="px-8 py-10 mx-auto max-w-7xl">

          {/* Available Modules Section */}
          <p className="mb-6 text-xs font-bold tracking-wider uppercase" style={{ color: PRIMARY }}>Available Modules</p>
          <div data-tour="modules" className="grid max-w-7xl grid-cols-1 gap-6 sm:grid-cols-3">
            <ModuleCard
              icon="/crm.png"
              title="Sales Management"
              description="Manage customer pipelines, track interactions, generate Companies and monitor your sales performance in real time."
              pills={["Pipeline", "Interactions", "Companies"]}
              onClick={() => handleOpenModule("", true, "https://sales.izsoftwares.com")}
            />
            <ModuleCard
              icon="/management.png"
              title="Hub"
              description="Manage employee metrics, payrolls, HR profiles and company-wide operational data from one central place."
              pills={["Employee Metrics", "Payroll", "Vacation"]}
              onClick={() => handleOpenModule("/Hub", true, "https://hub.izsoftwares.com")}
            />

            {/* Knowledge Center Card */}
            <ModuleCard
              icon={null}
              title="Knowledge Center"
              description="Access Zoho Connect — internal knowledge base, discussions, and team resources."
              pills={["Zoho Connect", "Knowledge Base", "Team Hub"]}
              onClick={() => window.open("https://connect.izsoftwares.com", "_blank", "noopener,noreferrer")}
            />
          </div>

          {/* Second row for additional modules */}
          <div className="grid max-w-7xl grid-cols-1 gap-6 mt-6 sm:grid-cols-3">
            {/* Zoho Cliq Card */}
            <ModuleCard
              icon={null}
              title="Zoho Cliq"
              description="Team communication and collaboration platform for instant messaging, channels, and video calls."
              pills={["Messaging", "Channels", "Video Calls"]}
              onClick={() => window.open("https://cliq.zoho.com/company/811742650", "_blank", "noopener,noreferrer")}
            />
          </div>

          {/* Horizontal Divider Line */}
          <div className="mt-12 mb-8">
            <div style={{
              height: "2px",
              background: `linear-gradient(90deg, transparent 0%, ${PRIMARY} 20%, ${PRIMARY} 80%, transparent 100%)`,
            }} />
          </div>

          {/* Products Section */}
          <div>
            <p className="mb-6 text-xs font-bold tracking-wider uppercase" style={{ color: PRIMARY }}>Our Products</p>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <ModuleCard
                icon="/ZetScore_Icon.png"
                title="ZetScore"
                description="ZetScore is an Employee Performance Management Software that equips your Organization with actionable, data-driven insights to enhance your employee performance."
                pills={["Launch →"]}
                onClick={() => window.open("https://test.zetscore.com", "_blank", "noopener,noreferrer")}
              />
              <ModuleCard
                icon="/Asset 33x.png"
                title="ZetCollect"
                description="Empowering African microfinance institutions with efficient, transparent, and fraud-resistant digital collection solutions"
                pills={["Launch →"]}
                onClick={() => window.open("https://app.zetcollect.com", "_blank", "noopener,noreferrer")}
              />
            </div>
          </div>
        </div>
      </main>

      <footer className="w-full py-4" style={{ borderTop: `1px solid ${PRIMARY_BORDER}` }}>
        <div className="flex flex-wrap items-center justify-between gap-2 px-8 mx-auto max-w-7xl">
          <span className="text-xs text-gray-400">© {new Date().getFullYear()} IZSoftwares &nbsp;·&nbsp; Internal Portal v2.1.0</span>
          <span className="text-xs font-medium" style={{ color: PRIMARY }}>{session.employeeId}</span>
        </div>
      </footer>

    </div>
  );
}